import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './assets/style.css';
import Table from 'components/Table';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';
import StatusTag from 'components/Atom/StatusTag';
import { useQuery } from 'react-query';
import { Empty, notification } from 'antd';
import { useSelector } from 'react-redux';
import { TIMEZONES, USER_ROLES } from 'utils/constant';
import { checkUserPermissions, formatAmount } from 'utils/services';
import { Button, ResetButton } from 'components/Atom/Button';
import { getInvoiceDetails, getInvoiceTransactions } from 'api/invoices';
import { formatInTimeZone } from 'date-fns-tz';
import PDF from './assets/pdf';
import { pdf } from '@react-pdf/renderer';
import { getInvoiceVariant, parseMerchantInvoiceDetailSegment } from './invoiceConfig';

const ViewMerchantInvoiceDetails = ({ invoiceType: invoiceTypeProp }) => {
  const location = useLocation();
  const { id: combinedId } = useParams();
  const { invoiceId: fromPathInvoice, storeId: fromPathStore } = parseMerchantInvoiceDetailSegment(combinedId);
  const variant = getInvoiceVariant(invoiceTypeProp || location?.state?.invoiceType);
  const listNavigationState = {
    id: location?.state?.id || fromPathInvoice,
    isRedirection: true,
    filtersData: location?.state?.filtersData,
    searchValue: location?.state?.searchValue,
    invoiceType: variant.invoiceType,
  };
  const store = useSelector((state) => state?.company?.store);
  const navigate = useNavigate();

  const record = location?.state?.record;
  const invoiceId = record?._id || fromPathInvoice || location?.state?.id;
  const storeId = fromPathStore || record?.store_id || store?._id;

  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 10,
    currentPage: 1,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);

  const tableContainerRef = useRef(null);
  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;

  const {
    data: pageData,
    refetch: refetchInvoicePage,
    isLoading,
    isFetching,
  } = useQuery(
    ['InvoiceDetailPage', variant.invoiceType, invoiceId, storeId],
    async () => {
      const resolvedInvoiceId = fromPathInvoice || record?._id || invoiceId;
      const resolvedStoreId = fromPathStore || record?.store_id || storeId;

      const detailsPromise = getInvoiceDetails(resolvedInvoiceId, resolvedStoreId).then((res) => res?.data);
      const transactionsPromise = resolvedStoreId
        ? getInvoiceTransactions({ store_id: resolvedStoreId, invoice_id: resolvedInvoiceId })
        : detailsPromise.then((details) =>
            getInvoiceTransactions({
              store_id: details?.store_id,
              invoice_id: resolvedInvoiceId,
            }),
          );

      const [details, txnResponse] = await Promise.all([detailsPromise, transactionsPromise]);
      const txnList = txnResponse?.data?.data ?? txnResponse?.data ?? [];

      return { details, transactions: txnList };
    },
    {
      enabled: !!invoiceId,
      refetchOnWindowFocus: false,
    },
  );

  const invoiceDetails = pageData?.details;
  const transactions = pageData?.transactions ?? [];

  const paginatedTransactions = useMemo(() => {
    const startIndex = (queryParams.currentPage - 1) * queryParams.limit;
    return transactions.slice(startIndex, startIndex + queryParams.limit);
  }, [transactions, queryParams.currentPage, queryParams.limit]);

  const handlePagePageChange = (page, pageSize) => {
    setQueryParams({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      currentPage: page,
    });
  };

  useEffect(() => {
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1 }));
  }, [combinedId]);

  const convertToCSV = (data) => {
    if (!data?.length) return '';
    const headers = ['Order / Payment ID', 'Account ID', 'Payment Intent ID', 'Price', 'Charges', 'Region Tax', 'W.H.T. Sales', 'W.H.T. Income', 'Merchant Amount', 'Time', 'Status'];

    const rows = data?.map((item) => [
      item?.order_id || 'N/A',
      item?.account_id || 'N/A',
      item?.payment_intent_id || 'N/A',
      item?.amount || 'N/A',
      item?.invoiceStats?.feeChargeAmount || '-',
      item?.invoiceStats?.regionTaxAmount || '-',
      item?.invoiceStats?.withHoldingSaleTaxAmount || '-',
      item?.invoiceStats?.withHoldingIncomeTaxAmount || '-',
      item?.invoiceStats?.netAmount || '-',
      item?.createdAt ? formatInTimeZone(item?.createdAt, 'Asia/Karachi', 'MMM d yyyy h:mm:ss a') : 'N/A',
      item?.status || 'N/A',
    ]);

    return [headers?.join?.(','), ...rows?.map?.((row) => row?.join?.(','))]?.join?.('\n');
  };

  const handleExport = () => {
    try {
      if (!invoiceId) {
        notification?.error({
          message: 'Invoice ID',
          description: 'Invoice ID not found',
          placement: 'bottomRight',
        });
        return;
      }

      if (!transactions?.length) {
        notification?.error({
          message: 'No Data',
          description: 'No invoice transactions available to export',
          placement: 'bottomRight',
        });
        return;
      }

      setExportLoading(true);
      const csv = convertToCSV(transactions);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document?.createElement('a');
      const url = URL?.createObjectURL(blob);

      const fileName = `InvoiceTransactions_${invoiceId}_${store?.name}`;

      link?.setAttribute('href', url);
      link?.setAttribute('download', fileName);
      document?.body?.appendChild(link);
      link?.click();
      link?.parentNode?.removeChild(link);

      notification?.success({
        message: 'Transactions Exported',
        description: 'Invoice transactions exported successfully',
        placement: 'bottomRight',
      });
    } catch (error) {
      console.error('Export invoice transactions error:', error?.message || error);
      notification?.error({
        message: 'Export Error',
        description: 'Failed to export invoice transactions',
        placement: 'bottomRight',
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handlePDFExport = async () => {
    setPdfExportLoading(true);
    try {
      if (!invoiceDetails) {
        throw new Error('No invoice data available');
      }

      const blob = await pdf(<PDF invoiceDetails={invoiceDetails} transactions={transactions} />).toBlob();

      const url = URL?.createObjectURL(blob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceDetails?._id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      notification?.success({
        message: 'PDF Exported',
        description: 'PDF exported successfully',
        placement: 'bottomRight',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      notification?.error({
        message: 'Error',
        description: 'Failed to generate PDF',
        placement: 'bottomRight',
      });
    } finally {
      setPdfExportLoading(false);
    }
  };

  const breadcrumbProps = {
    breadcrumbs: [
      { name: 'XPay', href: '/xpay' },
      {
        name: variant.breadcrumbListName,
        href: variant.basePath,
        data: listNavigationState,
      },
      { name: <ShortenId value={invoiceId || 'Not Found'} /> },
    ],
  };

  let invoiceDetailInfo = [
    { label: 'Invoice Number', value: <ShortenId value={invoiceDetails?._id} /> },
    { label: 'Status', value: invoiceDetails?.status ? <StatusTag tag={invoiceDetails?.status} /> : 'N/A' },
    { label: 'Account ID', value: <ShortenId value={invoiceDetails?.account_id} /> },
    { label: 'Amount', value: `${formatAmount(invoiceDetails?.invoice_net_amount || '0', 'PKR')} ` },
    { label: 'Created Date', value: invoiceDetails?.createdAt ? formatInTimeZone(invoiceDetails?.createdAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
  ];

  if (invoiceDetails?.status === 'settled') {
    invoiceDetailInfo?.splice(4, 0, {
      label: 'Settlement Date',
      value: invoiceDetails?.settlement_date ? formatInTimeZone(invoiceDetails?.settlement_date, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A',
    });
  }

  const invoiceSummaryInfo = [
    { label: 'Transactions Count', value: invoiceDetails?.captured_transaction_count || '0' },
    { label: 'Gross Amount', value: `${formatAmount(invoiceDetails?.invoice_gross_amount || '0', 'PKR')}` },
    { label: 'Charges', value: `${formatAmount(invoiceDetails?.rate_charged_amount || '0', 'PKR')}` },
    { label: 'Taxes', value: `${formatAmount(invoiceDetails?.tax_charged_amount || '0', 'PKR')}` },
    { label: 'Refund Count', value: invoiceDetails?.refunded_transaction_count || '0' },
    { label: 'Refund Amount', value: `${formatAmount(invoiceDetails?.refunded_amount || '0', 'PKR')}` },
    { label: 'Net Amount', value: `${formatAmount(invoiceDetails?.invoice_net_amount || '0', 'PKR')}` },
  ];

  const columns = [
    { title: 'Order / Payment ID', dataIndex: 'order_id', key: 'order_id' },
    {
      title: 'Payment Intent ID',
      dataIndex: 'payment_intent_id',
      key: 'paymentIntentId',
      render: (payment_intent_id) => (payment_intent_id ? <ShortenId value={payment_intent_id} /> : 'N/A'),
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (createdAt) => (createdAt ? formatInTimeZone(createdAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A'),
    },
    {
      title: 'Price',
      dataIndex: 'amount',
      key: 'price',
      render: (amount) => (amount ? formatAmount(amount, 'PKR') : 'N/A'),
    },
    {
      title: 'Charges',
      dataIndex: 'invoiceStats',
      key: 'charges',
      align: 'right',
      render: (invoiceStats) => formatAmount(invoiceStats?.feeChargeAmount, 'PKR'),
    },
    {
      title: 'Region Tax',
      dataIndex: 'invoiceStats',
      key: 'tax',
      align: 'right',
      render: (invoiceStats) => formatAmount(invoiceStats?.regionTaxAmount, 'PKR'),
    },
    {
      title: 'W.H.T. Sales',
      dataIndex: 'invoiceStats',
      key: 'withHoldingSaleTaxAmount',
      align: 'right',
      render: (invoiceStats) => formatAmount(invoiceStats?.withHoldingSaleTaxAmount, 'PKR'),
    },
    {
      title: 'W.H.T. Income',
      dataIndex: 'invoiceStats',
      key: 'withHoldingIncomeTaxAmount ',
      align: 'right',
      render: (invoiceStats) => formatAmount(invoiceStats?.withHoldingIncomeTaxAmount, 'PKR'),
    },
    {
      title: 'Merchant Amount',
      dataIndex: 'invoiceStats',
      key: 'netAmount',
      align: 'right',
      render: (invoiceStats) => formatAmount(invoiceStats?.netAmount, 'PKR'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusTag tag={status} />,
    },
  ];

  const showSelectStoreEmpty = !invoiceDetails && !isLoading && !fromPathStore && !storeId && !store?.isSelected;

  return (
    <>
      <div className='header-container p-4 custom-sticky'>
        <div className='payment-breadcrums'>
          <div
            className='back-button'
            onClick={() =>
              navigate(variant.basePath, {
                state: listNavigationState,
              })
            }>
            <ArrowLeftOutlined /> <span style={{ fontWeight: 600 }}>Back</span>
          </div>
          <span style={{ color: '#8C8C8C' }}>|</span>
          <Breadcrumbs {...breadcrumbProps} />
          <ResetButton onClick={refetchInvoicePage} loading={isFetching} />
        </div>

        <div className='heading-row'>
          <div className='detail-heading mb-4 mt-4'>{variant.detailHeading}</div>
          {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
            <Button loading={pdfExportLoading} disabled={pdfExportLoading} className='custom-btns p-2 flex items-center' onClick={handlePDFExport}>
              {pdfExportLoading === true ? null : <DownloadOutlined className='inline text-base' />} <span className='text-base'>Export PDF</span>
            </Button>
          )}
        </div>
        <div className='detail-heading mb-4'> {invoiceDetails?.status ? <StatusTag tag={invoiceDetails?.status} /> : 'N/A'}</div>
      </div>

      <div className='subscription-details logs-details'>
        {!invoiceDetails ? (
          showSelectStoreEmpty ? (
            <div className='empty-custom-style'>
              <Empty description={'Select store from the drop-down menu above'} />
            </div>
          ) : (
            <div className='empty-custom-style'>
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )
        ) : (
          <>
            <div className='details-section '>
              <div className='heading-section'>
                <h3>Invoice Details</h3>
              </div>
              <div className='detail-card'>
                {invoiceDetailInfo?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='details-section '>
              <div className='heading-section'>
                <h3>Invoice Summary</h3>
              </div>
              <div className='detail-card'>
                {invoiceSummaryInfo?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='details-section '>
              <div className='heading-section'>
                <h3>Transactions</h3>
                {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
                  <Button loading={exportLoading} disabled={exportLoading} className='custom-btns p-2 flex items-center' onClick={handleExport}>
                    {exportLoading === true ? null : <DownloadOutlined className='inline text-base' />} <span className='text-base'>Export</span>
                  </Button>
                )}
              </div>

              <div className='overflow-x-auto custom-table px-8 pt-6 inner-table' ref={tableContainerRef}>
                <Table
                  loading={isLoading}
                  dataSource={paginatedTransactions}
                  columns={columns}
                  totalLenght={transactions?.length}
                  pagingation={{
                    handlePageChange: handlePagePageChange,
                    perPageSize: queryParams?.limit,
                    page: queryParams?.currentPage,
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ViewMerchantInvoiceDetails;
